import { Box, Button, Flex, Icon } from '@everlywell/ui-kit';
import { X } from '@phosphor-icons/react';
import React from 'react';

import EvaText from '../assets/eva-wordmark.svg';
import { useChatMessagesContainer } from '../hooks/useChatMessagesContainer';
import { getMessageMetadata } from '../lib/message-helpers';
import type { ChatMessagesContainerProps } from '../types/components';
import ChatAssistantMessage from './ChatAssistantMessage';
import ChatTermsDisclaimer from './ChatTermsDisclaimer';
import ChatUserBubble from './ChatUserBubble';
import EvaThinkingIndicator from './internal/EvaThinkingIndicator';
import ChatProductRecommendations from './products/ChatProductRecommendations';

const MAX_WIDTH = 800;

const scrollSx = {
  '&::-webkit-scrollbar': { width: 8 },
  '&::-webkit-scrollbar-track': { background: 'transparent' },
  '&::-webkit-scrollbar-thumb': { background: '#E8E8E2', borderRadius: 5 },
  '&::-webkit-scrollbar-thumb:hover': { background: '#E8E8E2' },
  scrollbarWidth: 'thin',
  scrollbarColor: '#E8E8E2 transparent',
} as const;

const headerSx = {
  background:
    'linear-gradient(180deg, #F9F9F6 0%, rgba(249, 249, 246, 0.00) 100%)',
} as const;

const messageEntrySx = {
  animation: 'evaMessageEnter 260ms cubic-bezier(0.22, 1, 0.36, 1) both',
  willChange: 'transform, opacity',
  '@keyframes evaMessageEnter': {
    '0%': { opacity: 0, transform: 'translateY(6px)' },
    '100%': { opacity: 1, transform: 'translateY(0)' },
  },
  '@media (prefers-reduced-motion: reduce)': {
    animation: 'none',
    transform: 'none',
  },
} as const;

function Container({ children }: { children: React.ReactNode }) {
  return (
    <Flex
      width={MAX_WIDTH}
      maxWidth="100%"
      marginX="auto"
      justifyContent="center"
    >
      {children}
    </Flex>
  );
}

type RenderedMessage =
  | { key: string; node: React.ReactNode }
  | null;

function renderMessageNode(args: {
  message: ChatMessagesContainerProps['messages'][number];
  index: number;
  lastAgentMessageIndex: number;
  showLogoOnLastAgentMessage: boolean;
  isStreaming: boolean;
  activeAgentMessageId: string | null;
  activeStreamEventSeq: number;
  shouldAnimate: boolean;
  onTypingComplete?: () => void;
  shouldFadeOutAvatar?: boolean;
}): RenderedMessage {
  const {
    message,
    index,
    lastAgentMessageIndex,
    showLogoOnLastAgentMessage,
    isStreaming,
    activeAgentMessageId,
    activeStreamEventSeq,
    shouldAnimate,
    onTypingComplete,
    shouldFadeOutAvatar,
  } = args;

  const metadata = getMessageMetadata({
    message,
    index,
    lastAgentMessageIndex,
    showLogoOnLastAgentMessage,
    isStreaming,
    activeAgentMessageId,
  });

  if (metadata.shouldSkipRender) return null;

  if (message.isUser) {
    return { key: message.id, node: <ChatUserBubble text={message.text} /> };
  }

  return {
    key: message.id,
    node: (
      <Box width="100%">
        <ChatAssistantMessage
          text={message.text}
          showLogo={metadata.isLastAgentMessage}
          isStreaming={metadata.isActiveStreamingMessage}
          streamEventSeq={metadata.isActiveStreamingMessage ? activeStreamEventSeq : 0}
          shouldAnimate={shouldAnimate}
          onTypingComplete={onTypingComplete}
          shouldFadeOutAvatar={shouldFadeOutAvatar}
        />
      </Box>
    ),
  };
}

export default function ChatMessagesContainer(props: ChatMessagesContainerProps): JSX.Element {
  const {
    messages,
    isThinking,
    isStreaming,
    activeAgentMessageId,
    activeStreamEventSeq,
    recommendations,
    errorMessage,
    onClose,
    onSendMessage,
    onTypingComplete,
  } = props;

  const {
    lastAgentMessageIndex,
    showLogoOnError,
    adjustedShowLogoOnLastAgentMessage,
    isAwaitingAgentResponse,
    lastAgentMessageId,
    lastUserMessageId,
    lastAgentMessageRef,
    lastUserMessageRef,
    scrollRef,
    canShowRecommendations,
    lastMessageShouldAnimate,
    handleLearnMore,
    getMessageAnimationState,
  } = useChatMessagesContainer({
    messages,
    isThinking,
    isStreaming,
    recommendations,
    errorMessage,
    activeStreamEventSeq,
    activeAgentMessageId,
    onSendMessage,
    onTypingComplete,
  });

  const lastUserMessageIndex = React.useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      if (messages[i]?.isUser) return i;
    }
    return -1;
  }, [messages]);

  const renderedMessages = messages
    .flatMap((message, index) => {
      const {
        shouldAnimate,
        shouldFadeOutAvatar,
        onTypingComplete: handleMessageTypingComplete,
      } = getMessageAnimationState(message, index);

      const nodes: RenderedMessage[] = [];
      const renderedMessage = renderMessageNode({
        message,
        index,
        lastAgentMessageIndex,
        showLogoOnLastAgentMessage: adjustedShowLogoOnLastAgentMessage,
        isStreaming,
        activeAgentMessageId,
        activeStreamEventSeq,
        shouldAnimate,
        onTypingComplete: handleMessageTypingComplete,
        shouldFadeOutAvatar,
      });

      if (renderedMessage) {
        nodes.push(renderedMessage);
      }

      if (
        !message.isUser &&
        message.recommendations &&
        message.text.trim().length > 0
      ) {
        const hasNewerUserMessage = lastUserMessageIndex > index;
        const isLastAgentMessage = index === lastAgentMessageIndex;
        const shouldShowRecommendations = hasNewerUserMessage
          ? true
          : isLastAgentMessage
          ? canShowRecommendations
          : true;

        if (shouldShowRecommendations) {
          nodes.push({
            key: `rec-${message.id}`,
            node: (
              <Box width="100%">
                <ChatProductRecommendations
                  recommendations={message.recommendations}
                  showLogo={isLastAgentMessage}
                  shouldAnimate={isLastAgentMessage ? lastMessageShouldAnimate : false}
                  onLearnMore={handleLearnMore}
                  onCardClick={onClose}
                />
              </Box>
            ),
          });
        }
      }

      return nodes;
    })
    .filter((x): x is Exclude<RenderedMessage, null> => x !== null);

  return (
    <Box
      ref={scrollRef}
      position="absolute"
      inset="0"
      overflowY="auto"
      overflowX="hidden"
      marginBottom={100}
      sx={scrollSx}
    >
      <Box
        position="sticky"
        top="0 !important"
        zIndex={1}
        backgroundColor="tints.lightCream"
        height="48px"
      >
        <Container>
          <Flex padding="1rem">
            <Box
              as="img"
              src={EvaText}
              alt="eva"
              width="37.418px"
              height={12}
              sx={{ aspectRatio: '37.42 / 12' }}
            />
            <Button
              type="button"
              onClick={onClose}
              variant="ghost"
              position="absolute"
              top="8px"
              right="8px"
              width="2rem"
              height="2rem"
              minWidth="2rem"
              minHeight="2rem"
              padding="0.5rem"
              borderRadius="1.26875rem"
              backgroundColor="tints.white"
              color="eggplant.base"
              zIndex={2001}
              _hover={{ filter: 'brightness(0.97)' }}
              _active={{ filter: 'brightness(0.94)' }}
              aria-label="Close Eva"
            >
              <Icon
                as={X}
                aria-hidden="true"
                color="eggplant.base"
                boxSize={16}
              />
            </Button>
          </Flex>
        </Container>
        <Box
          height="32px"
          sx={headerSx}
        ></Box>
      </Box>

      <Flex
        width={MAX_WIDTH}
        maxWidth="100%"
        marginX="auto"
        flexDirection="column"
        alignItems="center"
        flexShrink={0}
        paddingTop={5}
        marginBottom={['65px', '50px']}
      >
        <Box width="100%" paddingX={4}>
          <ChatTermsDisclaimer />

          <Box width="100%" paddingTop={24} flex="0 0 auto">
            <Flex width="100%" flexDirection="column" gap="24px">
              {(() => {
                const lastUserMessageIndex = renderedMessages.findIndex(
                  ({ key }) => key === lastUserMessageId
                );
                const hasLastUserMessage = lastUserMessageIndex !== -1;

                const messagesBeforeLastUser = hasLastUserMessage
                  ? renderedMessages.slice(0, lastUserMessageIndex)
                  : renderedMessages;

                return (
                  <>
                    {messagesBeforeLastUser.map(({ key, node }) => (
                      <Box
                        key={key}
                        width="100%"
                        sx={messageEntrySx}
                        data-testid="eva-message-entry"
                        ref={key === lastAgentMessageId ? lastAgentMessageRef : undefined}
                      >
                        {node}
                      </Box>
                    ))}

                    {hasLastUserMessage && (
                      <Box
                        width="100%"
                        minHeight={['calc(100vh - 330px)','calc(100vh - 220px)']}
                        display="flex"
                        flexDirection="column"
                        alignItems="flex-start"
                        gap="24px"
                        ref={lastUserMessageRef}
                      >
                        {/* Constraint: Ensure last user message and response remain visible
                            in viewport. minHeight prevents content from collapsing above fold. */}
                        {renderedMessages.slice(lastUserMessageIndex).map(({ key, node }) => (
                          <Box
                            key={key}
                            width="100%"
                            sx={messageEntrySx}
                            data-testid="eva-message-entry"
                            ref={key === lastAgentMessageId ? lastAgentMessageRef : undefined}
                          >
                            {node}
                          </Box>
                        ))}

                        {errorMessage ? (
                          <Box
                            width="100%"
                            sx={messageEntrySx}
                            data-testid="eva-message-entry"
                          >
                            <ChatAssistantMessage
                              text={errorMessage}
                              showLogo={showLogoOnError}
                            />
                          </Box>
                        ) : null}

                        {isAwaitingAgentResponse ? (
                          <Box
                            width="100%"
                            data-testid="eva-message-entry"
                          >
                            <EvaThinkingIndicator />
                          </Box>
                        ) : null}
                      </Box>
                    )}

                    {!hasLastUserMessage && (
                      <>
                        {errorMessage ? (
                          <Box
                            width="100%"
                            sx={messageEntrySx}
                            data-testid="eva-message-entry"
                          >
                            <ChatAssistantMessage
                              text={errorMessage}
                              showLogo={showLogoOnError}
                            />
                          </Box>
                        ) : null}

                        {isAwaitingAgentResponse ? (
                          <Box
                            width="100%"
                            data-testid="eva-message-entry"
                          >
                            <EvaThinkingIndicator />
                          </Box>
                        ) : null}
                      </>
                    )}
                  </>
                );
              })()}
            </Flex>
          </Box>
        </Box>
      </Flex>
    </Box>
  );
}
